import type { ReactNode } from "react";

import { Group, UnstyledButton } from "@mantine/core";

import styles from "./NarrativeHeader.module.css";

export interface NarrativeHeaderProps {
    /** Product/plan name shown after the partner wordmark, e.g. "Home Device Protect". */
    productName: string;
    /** Partner wordmark rendered inline before the product name. Caller supplies the asset. */
    logo?: ReactNode;
    /** Second-line summary, e.g. "$25/month plus tax · Home tech coverage and support". */
    subtitle?: ReactNode;
    /** "View plan details" affordance (bottom-right). Hidden when omitted. */
    onViewPlanDetails?: () => void;
    viewPlanLabel?: string;
}

/**
 * The Long Form AI panel header — a product card matching the offer card shown on the previous step
 * of the flow (design: Ricardo 2026-08-27). Two lines: partner wordmark · product name on top, and
 * a price/coverage summary with "View plan details" beneath. Presentational; slots into the top of
 * `NarrativeContainer`.
 */
export const NarrativeHeader = ({
    productName,
    logo,
    subtitle,
    onViewPlanDetails,
    viewPlanLabel = "View plan details",
}: NarrativeHeaderProps) => (
    <div className={styles.card}>
        <div className={styles.title}>
            {logo && <span className={styles.logo}>{logo}</span>}
            {logo && (
                <span className={styles.sep} aria-hidden>
                    |
                </span>
            )}
            <span className={styles.productName}>{productName}</span>
        </div>

        <Group justify="space-between" align="flex-end" wrap="nowrap" gap="md" className={styles.meta}>
            <span className={styles.subtitle}>{subtitle}</span>
            {onViewPlanDetails && (
                <UnstyledButton className={styles.viewPlan} onClick={onViewPlanDetails}>
                    {viewPlanLabel}
                </UnstyledButton>
            )}
        </Group>
    </div>
);
